import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Box, ToggleButton, ToggleButtonGroup, useTheme } from "@mui/material";
import ModalGeneric from "./ModalGeneric";
import { PUT } from "../Generic/CrudRequest";
import EncryptionUtils from "../../Utils/EncryptionUtils";
import Alerts from "../../Utils/Alerts";

export default function LanguageButton({
  config,
  onLanguageChange,
  variant = "light",
}) {
  const { t, i18n } = useTranslation();
  const theme = useTheme();
  const [showModal, setShowModal] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState("");
  const [isUpdating, setIsUpdating] = useState(false);
  const [showAlert, setShowAlert] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertType, setAlertType] = useState("success");

  const primary = theme.palette.primary.main;
  const currentLanguageBase = (i18n.language || "es").split("-")[0];

  const pillSx =
    variant === "dark"
      ? {
          bgcolor: "rgba(255, 255, 255, 0.1)",
          borderRadius: 999,
          border: "1px solid rgba(255, 255, 255, 0.18)",
          "& .MuiToggleButton-root": {
            color: "rgba(255,255,255,0.85)",
            border: 0,
            px: 2.25,
            py: 0.5,
            fontWeight: 600,
            fontSize: "0.8rem",
            textTransform: "none",
            lineHeight: 1.2,
            minWidth: 44,
            "&.Mui-selected": {
              bgcolor: "rgba(255,255,255,0.95)",
              color: primary,
              "&:hover": { bgcolor: "rgba(255,255,255,0.95)" },
            },
            "&:hover": {
              bgcolor: "rgba(255,255,255,0.12)",
            },
            "&.Mui-disabled": {
              color: "rgba(255,255,255,0.35)",
            },
          },
        }
      : {
          bgcolor: "rgba(255, 255, 255, 0.95)",
          backdropFilter: "blur(12px)",
          borderRadius: 999,
          border: `1px solid rgba(7, 70, 142, 0.22)`,
          boxShadow: "0 2px 10px rgba(7, 70, 142, 0.12)",
          "& .MuiToggleButton-root": {
            color: primary,
            border: 0,
            px: 2.25,
            py: 0.65,
            fontWeight: 600,
            fontSize: "0.8rem",
            textTransform: "none",
            lineHeight: 1.2,
            minWidth: 44,
            "&.Mui-selected": {
              bgcolor: primary,
              color: "#fff",
              "&:hover": { bgcolor: primary },
            },
            "&:hover": {
              bgcolor: "rgba(7, 70, 142, 0.08)",
            },
            "&.Mui-disabled": {
              color: "rgba(7, 70, 142, 0.4)",
            },
          },
        };

  const handleToggleChange = (_, lang) => {
    if (!lang || lang === currentLanguageBase || isUpdating) return;
    setSelectedLanguage(lang);
    setShowModal(true);
  };

  const confirmLanguageChange = async () => {
    setIsUpdating(true);
    try {
      const userData = JSON.parse(EncryptionUtils.getDecryptedData("user"));
      const languageId = selectedLanguage === "es" ? 1 : 2;

      const response = await PUT(`${config.url}/api/User/updateLanguage`, {
        userId: userData.userId,
        languageId: languageId,
      });

      const apiLanguage = response?.data?.language || selectedLanguage;
      const apiLanguageBase = apiLanguage.split("-")[0];
      const updatedUserData = response?.data?.user || userData;

      if (response?.data?.user) {
        EncryptionUtils.storeEncryptedData("user", JSON.stringify(updatedUserData));
        if (updatedUserData.user?.permisosWebUsuarios) {
          localStorage.setItem(
            "permissions",
            JSON.stringify(updatedUserData.user.permisosWebUsuarios)
          );
        }
      } else {
        userData.idLanguage = languageId;
        userData.idLanguageNavigation = {
          idLanguage: languageId,
          descripcion: apiLanguageBase === "es" ? "Español" : "English",
          claveIdioma: apiLanguage,
        };
        EncryptionUtils.storeEncryptedData("user", JSON.stringify(userData));
      }

      await i18n.changeLanguage(apiLanguageBase);
      localStorage.setItem("language", apiLanguageBase);
      i18n.emit("languageChanged", apiLanguageBase);

      if (onLanguageChange) {
        onLanguageChange(updatedUserData);
      }

      setShowModal(false);
      setSelectedLanguage("");
      setAlertMessage(t("users.languageButton.updateSuccess"));
      setAlertType("success");
      setShowAlert(true);
    } catch (error) {
      console.error("Error al actualizar idioma:", error);
      setAlertMessage(t("users.languageButton.updateError"));
      setAlertType("error");
      setShowAlert(true);
    } finally {
      setIsUpdating(false);
    }
  };

  const cancelLanguageChange = () => {
    setShowModal(false);
    setSelectedLanguage("");
  };

  const languageLabel = (code) =>
    code === "es"
      ? t("users.languageButton.spanish")
      : t("users.languageButton.english");

  return (
    <Box>
      {showAlert && (
        <Alerts
          error={alertMessage}
          type={alertType}
          onClose={() => setShowAlert(false)}
        />
      )}

      <ToggleButtonGroup
        exclusive
        size="small"
        value={currentLanguageBase}
        onChange={handleToggleChange}
        disabled={isUpdating}
        aria-label={t("users.languageButton.changeTitle")}
        sx={pillSx}
      >
        <ToggleButton value="es">ES</ToggleButton>
        <ToggleButton value="en">EN</ToggleButton>
      </ToggleButtonGroup>

      <ModalGeneric
        open={showModal}
        title={t("users.languageButton.changeTitle")}
        description={t("users.languageButton.changeDescription", {
          current: languageLabel(currentLanguageBase),
          new: languageLabel(selectedLanguage),
        })}
        onClose={cancelLanguageChange}
        onAccept={confirmLanguageChange}
      />
    </Box>
  );
}
